// Two toast systems: global (window.showHxToast) for app-level events,
// local (per-component useState) for context-specific feedback.

import React, { useEffect, useState, useCallback, useRef } from 'react';
import './Toast.css';

/**
 * Global Toast — listens to window.showHxToast(message, kind, opts).
 * kind: 'success' | 'warning' | 'error' | 'info' (default: 'info')
 * opts: { duration?: number, action?: { label, onClick } }
 *
 * Backwards-compat: window.showUndoToast(message, onUndo) is also supported.
 */
export default function Toast() {
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);

  const dismiss = useCallback(() => {
    setToast(t => (t ? { ...t, leaving: true } : t));
    setTimeout(() => setToast(null), 220);
  }, []);

  const show = useCallback((message, kind = 'info', opts = {}) => {
    if (!message) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setToast({ id: Date.now(), message, kind, action: opts.action || null, leaving: false });
    const duration = typeof opts.duration === 'number' ? opts.duration : 1600;
    timerRef.current = setTimeout(dismiss, duration);
  }, [dismiss]);

  useEffect(() => {
    window.showHxToast = show;
    window.showUndoToast = (message, onUndo) =>
      show(message, 'info', { duration: 4000, action: onUndo ? { label: 'Undo', onClick: onUndo } : null });
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      delete window.showHxToast;
      delete window.showUndoToast;
    };
  }, [show]);

  if (!toast) return null;

  return (
    <div className="hx-toast-host" aria-live="polite" role="status">
      <div
        className={`hx-toast hx-toast-${toast.kind} ${toast.leaving ? 'hx-toast-leaving' : ''}`}
        key={toast.id}
      >
        <span className="hx-toast-dot" aria-hidden />
        <span className="hx-toast-msg">{toast.message}</span>
        {toast.action && (
          <button
            type="button"
            className="hx-toast-action"
            onClick={() => { toast.action.onClick?.(); dismiss(); }}
          >
            {toast.action.label}
          </button>
        )}
      </div>
    </div>
  );
}
